import { useCallback, useMemo } from "react"
import {
  ReactFlow,
  Background,
  type Node,
  type Edge,
  type NodeProps,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import {
  Users, Server, Database, HardDrive, Cpu, Layers, Box,
} from "lucide-react"
import { useSystemStore, type TopoNode, type NodeHealth } from "@/store/systemStore"

// ── Health colors ────────────────────────────────────────────────────────

const HEALTH_COLORS: Record<NodeHealth, { bg: string; border: string; glow: string; text: string; edge: string }> = {
  healthy:  { bg: "#0a1a0a", border: "#22c55e", glow: "0 0 12px rgba(34,197,94,0.25)",  text: "#22c55e", edge: "#22c55e" },
  degraded: { bg: "#1a1500", border: "#f59e0b", glow: "0 0 12px rgba(245,158,11,0.25)", text: "#f59e0b", edge: "#f59e0b" },
  critical: { bg: "#1a0a0a", border: "#ef4444", glow: "0 0 12px rgba(239,68,68,0.35)",  text: "#ef4444", edge: "#ef4444" },
  unknown:  { bg: "#111118", border: "#4b5563", glow: "none",                            text: "#6b7280", edge: "#4b5563" },
}

const NODE_ICONS: Record<string, typeof Users> = {
  users: Users,
  api: Server,
  queue: Layers,
  worker: Cpu,
  gpu: HardDrive,
  database: Database,
  storage: Box,
}

// ── Custom Node Component ────────────────────────────────────────────────

function SystemNode({ data }: NodeProps) {
  const topo = data as unknown as TopoNode & { onClick: (id: string) => void }
  const colors = HEALTH_COLORS[topo.health]
  const Icon = NODE_ICONS[topo.type] || Server
  const selectedNodeId = useSystemStore(s => s.selectedNodeId)
  const isSelected = selectedNodeId === topo.id

  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-transparent !border-0 !w-2 !h-2" />
      <div
        onClick={() => topo.onClick(topo.id)}
        className="cursor-pointer transition-all duration-200"
        style={{
          background: colors.bg,
          border: `1.5px solid ${isSelected ? "#818cf8" : colors.border}`,
          borderRadius: 12,
          padding: "10px 14px",
          minWidth: 110,
          boxShadow: isSelected ? "0 0 16px rgba(129,140,248,0.35)" : colors.glow,
        }}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Icon size={13} style={{ color: colors.text }} />
          <span className="text-[11px] font-medium text-gray-200">{topo.label}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-base font-mono font-bold" style={{ color: colors.text }}>
            {topo.metricValue}
          </span>
          <span className="text-[9px] text-gray-500">{topo.metric}</span>
        </div>
        {/* Health dot */}
        <div
          className={`absolute top-2 right-2 w-2 h-2 rounded-full ${topo.health !== "healthy" ? "animate-pulse" : ""}`}
          style={{ backgroundColor: colors.border }}
        />
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-transparent !border-0 !w-2 !h-2" />
    </>
  )
}

const nodeTypes = { system: SystemNode }

// ── Layout positions ─────────────────────────────────────────────────────

// Top-down layout: main pipeline flows vertically, branches spread horizontally
const NODE_POSITIONS: Record<string, { x: number; y: number }> = {
  users:    { x: 200, y: 0   },
  api:      { x: 200, y: 110 },
  redis:    { x: 200, y: 220 },
  worker:   { x: 200, y: 330 },
  gpu:      { x: 60,  y: 440 },
  storage:  { x: 340, y: 440 },
  postgres: { x: 420, y: 150 },
}

// ── Main Component ───────────────────────────────────────────────────────

export default function NetworkTopology() {
  const topoNodes = useSystemStore(s => s.topoNodes)
  const topoEdges = useSystemStore(s => s.topoEdges)
  const selectNode = useSystemStore(s => s.selectNode)

  const handleNodeClick = useCallback((nodeId: string) => {
    selectNode(nodeId)
  }, [selectNode])

  const flowNodes: Node[] = useMemo(() =>
    topoNodes.map(tn => ({
      id: tn.id,
      type: "system",
      position: NODE_POSITIONS[tn.id] || { x: 0, y: 0 },
      data: { ...tn, onClick: handleNodeClick },
      draggable: true,
    })),
    [topoNodes, handleNodeClick]
  )

  const flowEdges: Edge[] = useMemo(() =>
    topoEdges.map(te => {
      const colors = HEALTH_COLORS[te.health]
      return {
        id: te.id,
        source: te.source,
        target: te.target,
        animated: te.throughput > 0.1,
        label: te.label,
        labelStyle: { fill: "#6b7280", fontSize: 9, fontFamily: "monospace" },
        labelBgStyle: { fill: "#0a0a14", fillOpacity: 0.85 },
        labelBgPadding: [4, 2] as [number, number],
        style: {
          stroke: colors.edge,
          strokeWidth: 1 + te.throughput * 2.5,
          opacity: 0.7 + te.throughput * 0.3,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: colors.edge,
          width: 14,
          height: 14,
        },
      }
    }),
    [topoEdges]
  )

  if (topoNodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-600 text-sm">
        Loading topology...
      </div>
    )
  }

  return (
    <div className="h-full w-full relative" style={{ minHeight: 320 }}>
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        panOnDrag
        zoomOnScroll
        minZoom={0.4}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.85 }}
      >
        <Background color="#1a1a2e" gap={24} size={1} />
      </ReactFlow>
    </div>
  )
}
